import { setHours, setMinutes } from 'date-fns';

import { createForm } from '@components/form';
import { FormLabel } from '@components/form-label';
import { FormStoreInstance } from '@components/form/create-form-store';
import { IconEmail } from '@components/icons/email';
import { IconSms } from '@components/icons/phone-sms';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { createMinuteOptions, getHour, getMinute } from '@utils/hoursOptions';

import { JourneyActionDetailed, TravellerJourneyActionDetailed } from 'api/types';
import { FormData } from 'ui/actions/combo-send-form/traveller-sidebar';
import { ActionTimeSelector } from 'ui/actions/common';
import { useFeature } from 'ui/hooks';

const Form = createForm<FormData>();

export const EditRemindersContent = ({
  action,
  formStore,
  isFirstReminderCompleted,
  isRepeatRemindersCompleted,
}: {
  action: TravellerJourneyActionDetailed | JourneyActionDetailed;
  formStore: FormStoreInstance;
  isFirstReminderCompleted?: boolean;
  isRepeatRemindersCompleted?: boolean;
}) => {
  const { t } = useScopedTranslate(remindersLocale);
  const { isEnabled: hasSMSEnabled } = useFeature('FEATURE_SMS');

  const reminder = action.reminder;

  const values = formStore.useFormValues();
  const isEmailNotifyChecked = values['emailNotify'] as boolean;
  const isSmsNotifyChecked = values['smsNotify'] as boolean;

  const hasEmailReminder = values['emailReminderSwitch'] as boolean;
  const hasSmsReminder = values['smsReminderSwitch'] as boolean;
  const hasRepeat = values['repeatSwitch'] as boolean;

  const minuteOptions = createMinuteOptions(0, 59, 15);
  const defaultHour = 9;
  const defaultMinute = 0;

  const hour = values['hour'] as string;
  const minute = values['minute'] as string;

  const initialDate = setHours(setMinutes(new Date(), defaultMinute), defaultHour);
  const isRepeatDisabled =
    !(hasEmailReminder || hasSmsReminder) || !(isEmailNotifyChecked || isSmsNotifyChecked);

  return (
    <Stack space={4}>
      <Text font="regular14" color="textSecondary" text={t('description')} />
      <Stack space={6}>
        <Stack space={4}>
          <Stack space={4}>
            <Stack space={2}>
              <FormLabel text={t('reminderMethod')} />
              <RemindersMethod
                method="email"
                defaultValue={reminder?.emailNotify ?? false}
                isDisabled={!isEmailNotifyChecked}
                onChange={(value) => {
                  formStore.actions.setValue('emailReminderSwitch', value);
                  if (!value) {
                    formStore.actions.setValue('repeatSwitch', hasRepeat && hasSmsReminder);
                  }
                }}
              />
              {hasSMSEnabled && (
                <RemindersMethod
                  method="sms"
                  defaultValue={reminder?.smsNotify ?? false}
                  isDisabled={!isSmsNotifyChecked}
                  onChange={(value) => {
                    formStore.actions.setValue('smsReminderSwitch', value);
                    if (!value) {
                      formStore.actions.setValue('repeatSwitch', hasRepeat && hasEmailReminder);
                    }
                  }}
                />
              )}
            </Stack>
            {(hasEmailReminder || hasSmsReminder) && (
              <Stack space={2}>
                <Form.TextField
                  id="daysAfterSending"
                  label={t('daysAfterSending')}
                  validators={['positive-number']}
                  defaultValue={reminder?.days ?? '3'}
                  isDisabled={isFirstReminderCompleted}
                />
                <ActionTimeSelector
                  defaultHour={reminder?.hours ?? initialDate.getHours().toString()}
                  defaultMinute={reminder?.minutes ?? initialDate.getMinutes().toString()}
                  customMinuteOptions={minuteOptions}
                  isRequired={false}
                  isDisabled={isFirstReminderCompleted}
                />
                <Text
                  font="regular14"
                  text={
                    values['daysAfterSending'] === '1'
                      ? t('daysAfterSendingDescriptionOne', {
                          hour: getHour({ hour: hour, defaultHour: defaultHour }),
                          minute: getMinute({ minute: minute, defaultMinute: defaultMinute }),
                        })
                      : t('daysAfterSendingDescriptionMany', {
                          num_days: values['daysAfterSending']
                            ? values['daysAfterSending'].toString()
                            : 'X',
                          hour: getHour({ hour: hour, defaultHour: defaultHour }),
                          minute: getMinute({ minute: minute, defaultMinute: defaultMinute }),
                        })
                  }
                />
              </Stack>
            )}
          </Stack>
        </Stack>
        <Separator />
        <Stack space={4}>
          <Stack orientation="horizontal" justify="space-between">
            <Stack>
              <Text font="semibold14" text={t('repeat')} />
              {hasRepeat ? (
                <Text font="regular14" text={t('enabled')} />
              ) : (
                <Text font="regular14" text={t('disabled')} color="textHelper" />
              )}
            </Stack>
            <Form.Switch
              id="repeatSwitch"
              label={t('repeat')}
              defaultValue={Boolean(reminder?.frequency) ?? false}
              labelVisible={false}
              isDisabled={isRepeatDisabled}
            />
          </Stack>
          <Form.ConditionalValue id="repeatSwitch" when={(value) => Boolean(value)} animate>
            <Stack space={4}>
              <Stack space={2}>
                <Stack orientation="horizontal" space={1}>
                  <Text font="semibold14" text={t('frequency')} />
                  <Text font="regular14" text={t('daysInParentheses')} color="textHelper" />
                </Stack>
                <Form.TextField
                  id="frequency"
                  label={t('frequency')}
                  validators={['positive-number', 'required']}
                  hideLabel
                  defaultValue={reminder?.frequency ?? ''}
                  isDisabled={isRepeatRemindersCompleted}
                />
                <Text
                  font="regular14"
                  text={
                    values['frequency'] === '1'
                      ? t('frequencyDescriptionOne')
                      : t('frequencyDescriptionMany', {
                          num_days: values['frequency'] ? values['frequency'].toString() : 'X',
                        })
                  }
                />
              </Stack>
              <Stack space={2}>
                <Stack orientation="horizontal" space={1}>
                  <Text font="semibold14" text={t('amount')} />
                  <Text font="regular14" text={t('timesInParentheses')} color="textHelper" />
                </Stack>
                <Form.TextField
                  id="amount"
                  label={t('amount')}
                  validators={['positive-number', 'required']}
                  hideLabel
                  defaultValue={reminder?.amount ?? ''}
                  isDisabled={isRepeatRemindersCompleted}
                />
                <Text
                  font="regular14"
                  text={
                    values['amount'] === '1'
                      ? t('amountDescriptionOne')
                      : t('amountDescriptionMany', {
                          num_times: values['amount'] ? values['amount'].toString() : 'X',
                        })
                  }
                />
              </Stack>
            </Stack>
          </Form.ConditionalValue>
        </Stack>
      </Stack>
    </Stack>
  );
};

const RemindersMethod = ({
  method,
  defaultValue,
  isDisabled,
  onChange,
}: {
  method: 'email' | 'sms';
  defaultValue: boolean;
  isDisabled: boolean;
  onChange?: (value: boolean) => void;
}) => {
  const { t } = useScopedTranslate(remindersLocale);

  const icon =
    method === 'email' ? (
      <IconEmail color="elementPurple50" />
    ) : (
      <IconSms color="elementPurple50" />
    );
  const text = method === 'email' ? t('email') : t('sms');
  const method_id = method === 'email' ? 'emailReminderSwitch' : 'smsReminderSwitch';

  return (
    <Stack space={4} orientation="horizontal" align="center" justify="space-between">
      <Stack orientation="horizontal" space={1} align="center">
        {icon}
        <Text font="semibold14" text={text} color={isDisabled ? 'textHelper' : 'textPrimary'} />
      </Stack>
      <Form.Switch
        id={method_id}
        label={text}
        defaultValue={defaultValue ?? false}
        labelVisible={false}
        isDisabled={isDisabled}
        onChange={onChange}
      />
    </Stack>
  );
};

const remindersLocale = {
  en: {
    description: 'Send automatic reminders when form has not been completed',
    enabled: 'Enabled',
    disabled: 'Disabled',
    reminderMethod: 'Reminder method',
    email: 'Email',
    sms: 'SMS',
    daysAfterSending: 'Days after sending',
    daysAfterSendingDescriptionOne:
      'Sends a reminder the day after the form has been sent and has not been completed at {hour}:{minute}',
    daysAfterSendingDescriptionMany:
      'Sends a reminder {num_days} days after form has been sent and has not been completed at {hour}:{minute}',
    hour: 'Hour',
    minutes: 'Minute',
    repeat: 'Repeat reminders',
    frequency: 'Reminder Frequency',
    daysInParentheses: '(Days)',
    frequencyDescriptionOne:
      'Sends the reminder every day after the first reminder if the form has not been completed',
    frequencyDescriptionMany:
      'Sends the reminder every {num_days} days after the first reminder if the form has not been completed',
    amount: 'Max repeat reminders',
    timesInParentheses: '(Times)',
    amountDescriptionOne: 'Repeat reminder will only be sent once',
    amountDescriptionMany: 'Repeat reminders will be sent a maximum of {num_times} times',
  },
};
